import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { flushSync } from 'react-dom';
import type { DataRouter } from 'react-router-dom';

import { routerStateAtom } from '@/state/ui/shellLocationAtoms';
import type { Store } from '@/types/store';

/**
 * Feeds the router's location into `store` for as long as the calling component is mounted.
 *
 * The state is read again as the bridge subscribes: a child's effect can navigate before the
 * parent's effect runs. The shell navigates inside a flushSync of its own (app/panelUrl.ts), so
 * its store write renders in that flush. A navigation that asks for flushSync gets its store
 * write in flushSync too, because RouterProvider subscribes first and renders in flushSync.
 */
export function useRouterBridge(store: Store, router: DataRouter): void {
    const writeState = useSetAtom(routerStateAtom, { store });
    useEffect(() => {
        writeState(router.state);
        return router.subscribe((state, { flushSync: shouldSync }): void => {
            if (shouldSync) {
                flushSync(() => { writeState(state); });
            } else {
                writeState(state);
            }
        });
    }, [router, writeState]);
}
